import { useRef } from "react";
import { Link } from "react-router-dom";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValue,
  useSpring,
} from "framer-motion";
import { Star, Clock, ArrowRight } from "lucide-react";
import SEO from "../components/SEO";
import SectionHeading from "../components/SectionHeading";
import {
  Reveal,
  MaskedLines,
  Marquee,
  Sparkle,
  EASE,
} from "../components/Reveal";
import { ButtonLink, TextLink } from "../components/Buttons";
import FAQAccordion from "../components/FAQAccordion";
import MapSection from "../components/MapSection";
import CTASection from "../components/CTASection";
import GalleryGrid from "../components/GalleryGrid";
import {
  RatingSummary,
  ReviewCard,
  GoogleReviewsCTA,
} from "../components/Reviews";
import { site } from "../config/site";
import { categoryShowcase } from "../data/services";
import { galleryImages } from "../data/gallery";
import { faqs } from "../data/faqs";
import { reviews } from "../data/reviews";

const heroImg =
  "https://images.unsplash.com/photo-1562322140-8baeececf3df?auto=format&fit=crop&w=1200&q=80";
const experienceImg =
  "https://images.unsplash.com/photo-1560066984-138dadb4c035?auto=format&fit=crop&w=1600&q=80";

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "BeautySalon",
  name: site.fullName,
  url: "https://beauty-studio-hyd.preview.emergentagent.com",
  telephone: "+917337230662",
  image: heroImg,
  address: {
    "@type": "PostalAddress",
    streetAddress: "8-2-629/1/B, Road No. 12, Mithila Nagar",
    addressLocality: "Banjara Hills, Hyderabad",
    addressRegion: "Telangana",
    postalCode: "500034",
    addressCountry: "IN",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: site.geo.lat,
    longitude: site.geo.lng,
  },
  openingHours: site.hoursSchema,
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: site.rating.value,
    reviewCount: site.rating.count,
  },
  sameAs: Object.values(site.social).filter(Boolean),
};

const trustItems = [
  {
    icon: Sparkle,
    value: "Unisex Salon",
    label: site.addressShort,
    testId: "trust-unisex",
  },
  {
    icon: Star,
    value: `${site.rating.value} / 5`,
    label: `Google Rating · ${site.rating.count} reviews`,
    testId: "trust-rating",
  },
  {
    icon: Clock,
    value: "Open Daily",
    label: site.hoursTime,
    testId: "trust-hours",
  },
];

const whyItems = [
  {
    n: "01",
    title: "Personalised Approach",
    copy: "Every visit starts with understanding what you want — your hair, your style, your routine.",
  },
  {
    n: "02",
    title: "Unisex Experience",
    copy: "A welcoming salon for everyone. Walk in, feel at ease, leave feeling your best.",
  },
  {
    n: "03",
    title: "Convenient Banjara Hills Location",
    copy: "Easy to reach on Road No. 12, Mithila Nagar — in the heart of Banjara Hills, Hyderabad.",
  },
];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 55, damping: 18 });
  const sy = useSpring(my, { stiffness: 55, damping: 18 });

  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width - 0.5) * 16);
    my.set(((e.clientY - r.top) / r.height - 0.5) * 16);
  };

  return (
    <section
      ref={ref}
      onMouseMove={onMouseMove}
      className="relative flex min-h-[100svh] items-center overflow-hidden"
      data-testid="hero-section"
    >
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-10 left-0 select-none whitespace-nowrap font-display text-[22vw] italic leading-none text-outline"
      >
        Glow
      </span>
      <div className="container-x grid w-full items-center gap-14 pb-24 pt-40 lg:grid-cols-12 lg:gap-8 lg:pt-36">
        <motion.div className="lg:col-span-7" style={{ opacity: fade }}>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE }}
            className="flex items-center gap-4"
          >
            <span className="h-px w-10 bg-champagne" aria-hidden="true" />
            <p className="eyebrow">{site.tagline}</p>
          </motion.div>

          <h1 className="mt-7 font-display text-[clamp(3.2rem,8.5vw,7rem)] font-medium leading-[0.98] tracking-[-0.01em] text-ink">
            <MaskedLines
              immediate
              delay={0.15}
              lines={[
                <>Your Style.</>,
                <>
                  Your <em className="italic text-champagne">Glow.</em>
                </>,
                <>Your Signature.</>,
              ]}
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.7, ease: EASE }}
            className="mt-8 max-w-xl text-base leading-relaxed text-soften md:text-lg"
          >
            Discover professional hair and beauty services at Glow &amp; Blow
            Unisex Salon in Banjara Hills, Hyderabad. Explore our services, see
            the salon, read genuine client reviews and get in touch for your
            next appointment.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <ButtonLink to="/book" data-testid="hero-book-btn">
              Book an Appointment
            </ButtonLink>
            <ButtonLink to="/services" variant="outline" data-testid="hero-services-btn">
              Explore Services
            </ButtonLink>
          </motion.div>
        </motion.div>

        <div className="relative lg:col-span-5">
          <motion.div style={{ x: sx, y: sy }} className="img-frame relative">
            <motion.div
              initial={{ clipPath: "inset(100% 0 0 0)" }}
              animate={{ clipPath: "inset(0% 0 0 0)" }}
              transition={{ duration: 1.3, delay: 0.45, ease: EASE }}
              className="relative overflow-hidden"
            >
              <motion.img
                src={heroImg}
                alt="Stylist finishing a blow-dry at Glow & Blow Unisex Salon in Banjara Hills"
                width="960"
                height="1200"
                fetchPriority="high"
                className="aspect-[4/5] w-full object-cover"
                style={{ y: imgY, scale: 1.12 }}
                initial={{ scale: 1.28 }}
                animate={{ scale: 1.12 }}
                transition={{ duration: 1.6, delay: 0.45, ease: EASE }}
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.2, ease: EASE }}
              className="absolute -bottom-6 -left-6 hidden border border-line bg-ivory px-6 py-4 shadow-[0_20px_50px_-20px_rgba(23,23,23,0.3)] md:block"
            >
              <p className="eyebrow">Open Daily</p>
              <p className="mt-1 font-display text-xl text-ink">{site.hoursTime}</p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default function HomePage() {
  const expRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: expRef,
    offset: ["start end", "end start"],
  });
  const expY = useTransform(scrollYProgress, [0, 1], [-50, 50]);

  return (
    <>
      <SEO
        title="Glow & Blow Unisex Salon | Salon in Banjara Hills, Hyderabad"
        description="Glow & Blow Unisex Salon in Banjara Hills, Hyderabad. Explore salon services, view the gallery, read customer reviews and contact us for appointments."
        jsonLd={jsonLd}
      />

      <Hero />

      <Marquee
        items={["Hair", "Colour", "Beauty", "Styling", "Grooming"]}
      />

      {/* Trust strip */}
      <section className="border-b border-line bg-surface" data-testid="trust-strip">
        <div className="container-x grid divide-y divide-line md:grid-cols-3 md:divide-x md:divide-y-0">
          {trustItems.map((t, i) => (
            <Reveal key={t.testId} delay={i * 0.1}>
              <div
                className="flex items-center gap-5 py-8 md:justify-center md:py-10"
                data-testid={t.testId}
              >
                <t.icon className="h-6 w-6 shrink-0 text-champagne" />
                <div>
                  <p className="font-display text-2xl font-medium text-ink">
                    {t.value}
                  </p>
                  <p className="mt-0.5 text-xs uppercase tracking-[0.18em] text-soften">
                    {t.label}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Services */}
      <section className="py-24 md:py-32" data-testid="home-services">
        <div className="container-x">
          <div className="flex flex-wrap items-end justify-between gap-8">
            <SectionHeading
              index="01"
              eyebrow="What We Do"
              title={
                <>
                  Beauty &amp; Grooming, <em className="italic text-champagne">Your Way</em>
                </>
              }
            />
            <Reveal delay={0.2} className="hidden lg:block">
              <TextLink to="/services" data-testid="services-view-all">
                View All Services
              </TextLink>
            </Reveal>
          </div>
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {categoryShowcase.map((c, i) => (
              <Reveal key={c.name} delay={i * 0.08}>
                <Link
                  to="/services"
                  data-testid={`home-service-${c.name.toLowerCase()}`}
                  className="group relative block overflow-hidden border border-line"
                >
                  <div className="aspect-[3/4] overflow-hidden">
                    <img
                      src={c.image}
                      alt={c.alt}
                      loading="lazy"
                      width="600"
                      height="800"
                      className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/15 to-transparent" />
                  <span className="absolute left-5 top-5 font-display text-base italic text-champagne">
                    0{i + 1}
                  </span>
                  <div className="absolute inset-x-5 bottom-5">
                    <h3 className="font-display text-3xl font-medium text-ivory">
                      {c.name}
                    </h3>
                    <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-ivory/70">
                      {c.description}
                    </p>
                    <span className="mt-4 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-champagne">
                      View Services
                      <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-10 lg:hidden">
            <TextLink to="/services">View All Services</TextLink>
          </Reveal>
        </div>
      </section>

      {/* Why choose us */}
      <section className="border-y border-line bg-surface py-24 md:py-32" data-testid="why-choose">
        <div className="container-x">
          <SectionHeading
            index="02"
            eyebrow="Why Glow & Blow"
            title={
              <>
                A Salon Experience <em className="italic text-champagne">Made Around You</em>
              </>
            }
          />
          <div className="mt-14 grid gap-10 md:grid-cols-3 md:gap-8">
            {whyItems.map((w, i) => (
              <Reveal key={w.n} delay={i * 0.12}>
                <div className="border-t-2 border-ink pt-8" data-testid={`why-card-${i}`}>
                  <span className="font-display text-5xl italic leading-none text-champagne">
                    {w.n}
                  </span>
                  <h3 className="mt-6 font-display text-2xl font-medium text-ink md:text-[1.7rem]">
                    {w.title}
                  </h3>
                  <p className="mt-4 text-base leading-relaxed text-soften">
                    {w.copy}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Salon experience */}
      <section
        ref={expRef}
        className="grain-dark relative overflow-hidden bg-ink py-24 md:py-36"
        data-testid="experience-section"
      >
        <div className="container-x grid items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <Reveal>
              <div className="overflow-hidden">
                <motion.img
                  src={experienceImg}
                  alt="Inside the Glow & Blow salon space in Banjara Hills"
                  loading="lazy"
                  width="1400"
                  height="900"
                  style={{ y: expY, scale: 1.15 }}
                  className="aspect-[16/11] w-full object-cover"
                />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-6 lg:pl-10">
            <Reveal>
              <div className="flex items-center gap-4">
                <p className="eyebrow-light">The Experience</p>
                <span className="h-px w-12 bg-champagne/50" aria-hidden="true" />
              </div>
            </Reveal>
            <h2 className="mt-6 font-display text-[clamp(2.2rem,4.5vw,3.8rem)] font-medium leading-[1.04] text-ivory">
              <MaskedLines
                lines={[
                  <>Step In.</>,
                  <>
                    Slow Down. <em className="italic text-champagne">Glow Up.</em>
                  </>,
                ]}
              />
            </h2>
            <Reveal delay={0.25}>
              <p className="mt-6 max-w-lg text-base leading-relaxed text-ivory/70 md:text-lg">
                From a fresh haircut to a complete beauty refresh, every visit
                should feel easy, comfortable and personal.
              </p>
            </Reveal>
            <Reveal delay={0.4}>
              <div className="mt-10">
                <ButtonLink to="/about" variant="gold" data-testid="experience-cta">
                  Discover Our Salon
                </ButtonLink>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Gallery preview */}
      <section className="py-24 md:py-32" data-testid="home-gallery">
        <div className="container-x">
          <div className="flex flex-wrap items-end justify-between gap-8">
            <SectionHeading
              index="03"
              eyebrow="The Gallery"
              title={
                <>
                  A Look <em className="italic text-champagne">Inside</em>
                </>
              }
            />
            <Reveal delay={0.2}>
              <TextLink to="/gallery" data-testid="gallery-view-all">
                View Full Gallery
              </TextLink>
            </Reveal>
          </div>
          <div className="mt-14">
            <GalleryGrid items={galleryImages.slice(0, 6)} />
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="border-y border-line bg-surface py-24 md:py-32" data-testid="home-reviews">
        <div className="container-x grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <SectionHeading
              index="04"
              eyebrow="Client Reviews"
              title={
                <>
                  Loved by Clients. <em className="italic text-champagne">See What They Say.</em>
                </>
              }
            />
            <Reveal delay={0.2} className="mt-10">
              <RatingSummary />
            </Reveal>
            <Reveal delay={0.3}>
              <GoogleReviewsCTA className="mt-8" />
            </Reveal>
          </div>
          <div className="grid content-start gap-5 sm:grid-cols-2 lg:col-span-8 xl:grid-cols-3">
            {reviews.map((r, i) => (
              <ReviewCard key={r.id} review={r} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Location */}
      <section className="py-24 md:py-32" data-testid="home-location">
        <div className="container-x">
          <SectionHeading
            index="05"
            eyebrow="Visit Us"
            title={
              <>
                Visit Us in <em className="italic text-champagne">Banjara Hills</em>
              </>
            }
            copy={`${site.addressLine1}, ${site.addressLine2}`}
            className="mb-14"
          />
          <MapSection />
        </div>
      </section>

      {/* FAQ */}
      <section className="pb-24 md:pb-32" data-testid="home-faq">
        <div className="container-x">
          <SectionHeading
            index="06"
            eyebrow="Questions"
            title={
              <>
                Before You <em className="italic text-champagne">Visit</em>
              </>
            }
            className="mb-12"
          />
          <FAQAccordion items={faqs.slice(0, 6)} idPrefix="home-faq" />
          <Reveal className="mt-10">
            <TextLink to="/faq" data-testid="faq-view-all">
              View All FAQs
            </TextLink>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </>
  );
}
